import React, { useState } from 'react';
import Select from 'react-select';
import Avatar from '@material-ui/core/Avatar';

import { injectIntl, defineMessages } from 'react-intl';
import styles from './styles.module.css';
import AccessControlsMenu from './AccessControlsMenu';
import { getAvatar } from '../../../utils';

const messages = defineMessages({
  SUBSCRIBER_GROUP: {
    id: 'SUBSCRIBER_GROUP',
    defaultMessage: 'Users',
  },
  SITE_GROUP: {
    id: 'SITE_GROUP',
    defaultMessage: 'Sites',
  },
  SCOPE_GROUP: {
    id: 'SCOPE_GROUP',
    defaultMessage: 'Scopes',
  },
  DEPARTMENT_GROUP: {
    id: 'DEPARTMENT_GROUP',
    defaultMessage: 'Departments',
  },
  CALLQUEUE_GROUP: {
    id: 'CALLQUEUE_GROUP',
    defaultMessage: 'Call Queues',
  },
  CALL_CENTER_SUPERVISOR: {
    id: 'CALL_CENTER_SUPERVISOR',
    defaultMessage: 'All Call Center Supervisors',
  },
  CALL_CENTER_AGENT: {
    id: 'CALL_CENTER_AGENT',
    defaultMessage: 'All Call Center Agents',
  },
});

const groupStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const groupBadgeStyles = {
  backgroundColor: '#EBECF0',
  borderRadius: '2em',
  color: '#172B4D',
  display: 'inline-block',
  fontSize: 12,
  fontWeight: 'normal',
  lineHeight: '1',
  minWidth: 1,
  padding: '0.16666666666667em 0.5em',
  textAlign: 'center',
};

function AutoComplete({
  handleAutocomplete,
  intl,
  isGroupedOptions,
  isMulti,
  options,
  placeholder,
  selectedOptions,
  showAccessControls,
}) {
  let opts = options;

  const [toggleState, setToggleState] = useState(true);

  // custom html for group labels
  const formatGroupLabel = (data) => (
    <div style={groupStyles}>
      <span>
        {data.label}
      </span>
      <span style={groupBadgeStyles}>
        {data.options.length}
      </span>
    </div>
  );

  // custom html for select options
  const formatOption = (props) => {
    const option = props.data;
    const avatar = getAvatar(option, styles);

    if (option.type === 'scope') {
      const messageId = option.value.toUpperCase().replace(/ /g, '_');
      option.name = intl.formatMessage({ ...messages[messageId] });
    }

    let label = '';
    if (option.type === 'callqueue') {
      label = (
        <div style={{ opacity: 1 }} className={option.desc ? styles.optionLabelHalf : styles.optionLabelFull}>
          {option.desc || option.name}
        </div>
      );
    } else {
      label = (
        <div style={{ opacity: 1 }} className={option.desc ? styles.optionLabelHalf : styles.optionLabelFull}>
          {option.name || option.value}
        </div>
      );
    }

    let desc = '';
    if (option.desc) {
      if (option.type === 'callqueue') {
        desc = (
          <div className={styles.optionDesc}>
            <span>{option.name || option.value}</span>
          </div>
        );
      } else {
        desc = (
          <div className={styles.optionDesc}>
            <span>{option.desc}</span>
          </div>
        );
      }
    }

    // for some reason, I couldn't get this style to apply via a styles.module.css declaration
    // but doing it inline like this works. the :hover selector is still in styles.module.css
    const isFocusedStyle = props.isFocused ? { backgroundColor: '#dedede' } : {};

    return (
      <div ref={props.innerRef} style={isFocusedStyle} className={styles.autocompleteOption} {...props.innerProps}>
        <div className={styles.optionAvatar}>
          {avatar}
        </div>
        <div className={styles.optionContent}>
          {label}
          {desc}
        </div>
      </div>
    );
  };

  // custom html for selected item container
  const multiValueContainer = (props) => (
    <div {...props.innerProps} className={styles.selectedOptionContainer}>
      {getAvatar(props.data, styles)}
      {props.children}
    </div>
  );

  // custom html for single selected item chip
  const formatSingleValue = (inbound) => (
    <div {...inbound.innerProps} className={styles.selectedOptionContainer}>
      {getAvatar(inbound.data, styles)}
      <span className={styles.selectedOptionChip}>
        {(inbound.data.name || inbound.data.value).slice(0, 24)}
      </span>
    </div>
  );

  const toggleParentClick = (state) => {
    setToggleState(state);
    document.activeElement.blur();
  };

  // custom html for selected item chips
  const formatMultiValue = (inbound) => {
    let displayStr = inbound.data.name || inbound.data.value;
    if (displayStr.length > 25) { displayStr = `${displayStr.slice(0, 22)}...`; }

    return (
      <span {...inbound.innerProps} className={styles.selectedOptionChip}>
        {inbound.data.gravatar && <Avatar src={inbound.data.gravatar} />}
        {displayStr}
        {showAccessControls && (
          <AccessControlsMenu toggleParentClick={toggleParentClick} option={inbound.data} />
        )}
      </span>
    );
  };

  // step through an option's data properties and see if any values contain 'search'
  const filterOption = (option, search) => {
    if (!search) return true;

    let matchFound = false;
    Object.keys(option.data).forEach((key) => {
      if (option.data[key] && option.data[key].toLowerCase().includes(search.toLowerCase())) { // does prop include search
        matchFound = true;
      }
    });

    return matchFound;
  };

  const handleMenuOpen = () => toggleState;

  if (isGroupedOptions && options) {
    const groupedOptions = [];

    Object.keys(options).forEach((key) => {
      const keyid = `${key.replace(/ /g, '_').toUpperCase()}_GROUP`;
      const str = { ...messages[keyid] };
      if (str && str.defaultMessage) {
        groupedOptions.push({
          label: intl.formatMessage(str),
          options: options[key],
        });
      }
    });

    opts = groupedOptions;
  }

  if (opts) {
    return (
      <Select
        isClearable
        isSearchable
        isDisabled={!toggleState}
        openMenuOnFocus={toggleState}
        openMenuOnClick={toggleState}
        className={styles.autocompleteSelect}
        options={opts}
        value={selectedOptions}
        isMulti={isMulti}
        onMenuOpen={handleMenuOpen}
        onChange={handleAutocomplete}
        placeholder={placeholder}
        formatGroupLabel={formatGroupLabel}
        filterOption={filterOption}
        components={{
          Option: formatOption,
          SingleValue: formatSingleValue,
          MultiValueContainer: multiValueContainer,
          MultiValueLabel: formatMultiValue,
        }}
      />
    );
  }

  return (
    <p>
      . . .
    </p>
  );
}

export default injectIntl(AutoComplete);
